'use client';
/* eslint-disable react/no-array-index-key */
/* eslint-disable tailwindcss/no-arbitrary-value */
import classNames from 'classnames';
import { PropsWithChildren } from 'react';
import { BaseTranslations } from '../../baseTranslations';
import { CmsProduct } from '../../types/cmsProduct';
import { VerticalType } from '../../types/cmsTypes';
import { PushDataLayerFn } from '../../types/pushDataLayer';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { groupProductAttributes } from '../../utils/helpers';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { tr } from '../../utils/translations/tr';
import AwardIcon from '../icons/AwardIcon';
import { MaterialIcon } from '../MaterialIcon';
import { ProductCardOffer } from '../ProductCardOffer';
import { RichText } from '../RichText';
import AttributeKeyValue from './AttributeKeyValue';
import AttributePayment from './AttributePaymentType';
import { ProductApplyCta } from './ProductApplyCta';
import ProductCardSticky from './ProductCardSticky';
import ProductVerticalTable from './ProductVerticalTable';

type ProductContentProps = {
  vertical?: VerticalType;
  product: CmsProduct;
  slug?: string;
  ctaLinkOverride: string | undefined;
  pushDataLayer: PushDataLayerFn;
};

const ProductContent: React.FC<ProductContentProps> = ({
  product,
  vertical,
  ctaLinkOverride,
  pushDataLayer,
}) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(BaseTranslations, context);

  const blockClasses = classNames(
    'my-4 px-0 last:mb-0 md:mb-6 md:px-8 lg:px-10 lg:!pl-0',
  );
  const welcomeOffers = product.welcome_offers?.filter((o) => !!o);
  const exclusiveOffers = [
    ...(product.exclusive_offers ?? []),
    ...(product.bundle_exclusive_offers ?? []),
  ].filter((o) => !!o);
  const productAttributesGroup = groupProductAttributes(product);
  const rewardProductTitle =
    vertical === 'CREDIT_CARD'
      ? staticText.brand_reward_product_modal_cc
      : staticText.brand_reward_product_modal;
  // const ctaLinkOverride = metConfigCmsData?.product_card
  //   ?.filter((item) => !!item?.productBlock?.product?.title)
  //   .find((item) => item?.productBlock?.product?.title === product.title)
  //   ?.productBlock?.override_cta_link;

  return (
    <div>
      <div className="mx-auto max-w-[1536px] p-4 md:p-6">
        <div className="relative lg:flex">
          <ProductCardSticky
            {...product}
            product={product}
            ctaLinkOverride={ctaLinkOverride}
            pushDataLayer={pushDataLayer}
          />
          <div className="xs:pb-30 overflow-x-hidden py-8 md:w-full md:pb-8">
            <div className="md:mr-6">
              <h3 className="mb-4 hidden font-bold text-gray-900 text-2xl leading-line-height/2xl sm:mb-6 sm:text-3xl sm:leading-line-height/3xl md:mb-6 md:text-4xl md:leading-line-height/4xl lg:block">
                {product.product_name ?? ''}
              </h3>
              <RichText className="hidden font-medium text-gray-900 text-base md:text-xl md:leading-line-height/4xl lg:block">
                {product.description ?? ''}
              </RichText>
              {product.attributes?.map((attr, index) => {
                return (
                  <div key={index} className={blockClasses}>
                    {attr?.__typename === 'ComponentAttributesAttrKeyValue' &&
                      attr.label === 'Quick Facts' &&
                      Array.isArray(attr.value) &&
                      attr.value.length > 0 && (
                        <AttributeKeyValue {...attr} vertical={vertical} />
                      )}
                  </div>
                );
              })}
              {product.attributes?.map((attr, index) => {
                return (
                  <div key={index} className={blockClasses}>
                    {attr?.__typename === 'ComponentAttributesAttrKeyValue' &&
                      attr.label !== 'Quick Facts' &&
                      Array.isArray(attr.value) &&
                      attr.value.length > 0 && (
                        <AttributeKeyValue {...attr} vertical={vertical} />
                      )}
                  </div>
                );
              })}
              {productAttributesGroup?.benefits &&
                productAttributesGroup?.benefits?.length > 0 && (
                  <div className={blockClasses}>
                    <ProductVerticalTable
                      data={productAttributesGroup?.benefits ?? []}
                      provider={product.provider?.provider_name}
                      title={tr('benefits', staticText)}
                      pushDataLayer={pushDataLayer}
                    />
                  </div>
                )}
              {productAttributesGroup?.eligibility &&
                productAttributesGroup?.eligibility?.length > 0 && (
                  <div className={blockClasses}>
                    <ProductVerticalTable
                      data={productAttributesGroup?.eligibility ?? []}
                      provider={product.provider?.provider_name}
                      title={tr('eligibility', staticText)}
                      pushDataLayer={pushDataLayer}
                    />
                  </div>
                )}
              <div className="my-4 px-0 last:mb-0 md:mb-6 md:px-8 lg:px-10 lg:!pl-0">
                {product.long_description && (
                  <WrapperInfoBlock
                    icon={<AwardIcon />}
                    title={staticText.long_description}
                  >
                    <div className="text-gray-800 alpha-body-2">
                      <RichText>{product.long_description}</RichText>
                    </div>
                  </WrapperInfoBlock>
                )}
              </div>
              {((welcomeOffers?.length ?? 0) > 0 ||
                (exclusiveOffers?.length ?? 0) > 0) && (
                <div className={blockClasses}>
                  <WrapperInfoBlock
                    id="#singsaver_rewards_section"
                    icon={<MaterialIcon icon="diamond" size={3} />}
                    title={rewardProductTitle}
                    showWithoutChildren
                  />
                  {[...(exclusiveOffers || []), ...(welcomeOffers || [])]
                    .length ? (
                    <div className="flex flex-col gap-shapes/pictogram-md rounded-b-2xl border-t-2 border-t-brand-primary-dark p-6 shadow bg-fill/primary-light xl:rounded-b-3xl">
                      <div className="flex w-full flex-col">
                        <div className="flex flex-col gap-4 alpha-body-2">
                          {exclusiveOffers?.map((offer) => (
                            <ProductCardOffer
                              key={offer.offer_title}
                              {...offer}
                            />
                          ))}
                          {welcomeOffers?.map((offer) => (
                            <ProductCardOffer
                              key={offer.offer_title}
                              {...offer}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
              <div className={blockClasses}>
                <ProductVerticalTable
                  data={productAttributesGroup?.fees ?? []}
                  provider={product.provider?.provider_name}
                  title={tr('fees', staticText)}
                  pushDataLayer={pushDataLayer}
                />
              </div>
              <div className={blockClasses}>
                <ProductVerticalTable
                  data={productAttributesGroup?.repayment ?? []}
                  provider={product.provider?.provider_name}
                  title={tr('repayment', staticText)}
                  pushDataLayer={pushDataLayer}
                />
              </div>
              {product.attributes?.map((attr, index) => {
                return (
                  <div key={index} className={blockClasses}>
                    {attr?.__typename ===
                      'ComponentAttributesAttrPaymentNetwork' && (
                      <AttributePayment {...attr} />
                    )}
                  </div>
                );
              })}
              {product.attributes?.map((attr, index) => {
                return (
                  <div key={index} className={blockClasses}>
                    {attr?.__typename ===
                      'ComponentAttributesAttrPaymentType' && (
                      <AttributePayment {...attr} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <ProductApplyCta
        className="sticky bottom-0 p-0 lg:hidden"
        product={product}
        ctaLinkOverride={ctaLinkOverride}
        pushDataLayer={pushDataLayer}
      />
    </div>
  );
};

export default ProductContent;

export const WrapperInfoBlock: React.FC<WrapperInfoBlockProps> = ({
  id,
  icon,
  title,
  children,
  showWithoutChildren = false,
}) => {
  if (!children && !showWithoutChildren) {
    return null;
  }
  return (
    <div id={id}>
      <h4 className="mb-2 flex items-center md:mb-4">
        <div className="mr-2 size-6 !text-brand-primary-dark [&>*:first-child]:size-6">
          {icon}
        </div>
        <span className="block font-semibold text-brand-primary-dark text-base leading-line-height/badge md:text-xl md:leading-line-height/3xl">
          {title}
        </span>
      </h4>

      {children && (
        <div className="rounded-b-2xl border-t-2 border-t-brand-primary-dark p-4 shadow bg-white md:rounded-b-3xl md:p-6">
          {children}
        </div>
      )}
    </div>
  );
};

type WrapperInfoBlockProps = PropsWithChildren & {
  id?: string;
  title: string;
  icon: JSX.Element;
  showWithoutChildren?: boolean;
};
